import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Loader2, X, Send } from "lucide-react";
import { getUser } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MESSAGE_MAX = 300;

/** Carte sombre « Invitation privée » + formulaire : un visiteur propose un rendez-vous. */
export const InvitationPrivee = ({ annonceId, nom, photo }) => {
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [form, setForm] = useState({
    visitor_name: "", visitor_whatsapp: "", datetime: "", message: "", majeur: false,
  });

  const maj = (cle) => (e) =>
    setForm((f) => ({ ...f, [cle]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API}/invitations`, {
        annonce_id: annonceId,
        ...form,
        viewer_id: getUser()?.id || null,
      });
      toast.success(data.message);
      setOuvert(false);
      setForm({ visitor_name: "", visitor_whatsapp: "", datetime: "", message: "", majeur: false });
      if (data.lien_whatsapp) window.open(data.lien_whatsapp, "_blank", "noopener");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible pour le moment.");
    } finally {
      setEnvoi(false);
    }
  };

  const champ = "w-full rounded-xl border border-white/15 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#E05638]";

  return (
    <>
      <button
        type="button"
        onClick={() => setOuvert(true)}
        data-testid="invitation-privee-carte"
        className="group flex w-full items-center gap-3.5 rounded-2xl border border-white/10 bg-[#12100E] p-4 text-left transition-colors hover:border-[#E05638]/60"
      >
        {photo ? (
          <img src={photo} alt={nom} className="h-12 w-12 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="font-heading flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#E05638]/20 text-lg font-extrabold text-[#E05638]">
            {(nom || "?").charAt(0)}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-bold uppercase tracking-wider text-[#E05638]">
            Invitation privée
          </span>
          <span className="mt-0.5 block truncate text-sm font-semibold text-white">
            Passez du temps avec {nom}
          </span>
          <span className="block text-xs text-stone-400">Elle le recevra sur WhatsApp</span>
        </span>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E05638] text-white transition-transform group-hover:translate-x-0.5">
          <ChevronRight size={18} />
        </span>
      </button>

      {ouvert && (
        <div
          className="fixed inset-0 z-[200] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4"
          data-testid="invitation-privee-modal"
        >
          <form
            onSubmit={envoyer}
            className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl border border-white/10 bg-[#12100E] p-6 sm:max-w-md sm:rounded-3xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-extrabold text-white">Invitation privée</h3>
                <p className="mt-1 text-xs text-stone-400">
                  {nom} recevra votre invitation sur WhatsApp.
                </p>
              </div>
              <button type="button" onClick={() => setOuvert(false)} data-testid="invitation-fermer"
                className="rounded-full p-1.5 text-stone-400 hover:bg-white/10 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 space-y-3">
              <input required value={form.visitor_name} onChange={maj("visitor_name")}
                placeholder="Votre prénom" data-testid="invitation-prenom" className={champ} />
              <input required value={form.visitor_whatsapp} onChange={maj("visitor_whatsapp")}
                placeholder="Votre WhatsApp avec indicatif (+225, +33...)"
                data-testid="invitation-whatsapp" className={champ} />
              <input type="datetime-local" value={form.datetime} onChange={maj("datetime")}
                data-testid="invitation-date" className={champ} />
              <textarea rows={3} maxLength={MESSAGE_MAX} value={form.message} onChange={maj("message")}
                placeholder="Votre message (facultatif)" data-testid="invitation-message"
                className={`${champ} resize-none`} />
              <p className="text-right text-[11px] text-stone-500">
                {form.message.length}/{MESSAGE_MAX}
              </p>
              <label className="flex items-start gap-2.5 text-xs text-stone-300">
                <input type="checkbox" required checked={form.majeur} onChange={maj("majeur")}
                  data-testid="invitation-majeur" className="mt-0.5 h-4 w-4 accent-[#E05638]" />
                J'ai 18 ans ou plus.
              </label>
            </div>

            <button type="submit" disabled={envoi} data-testid="invitation-envoyer"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E05638] py-3 text-sm font-bold text-white transition-colors hover:bg-[#c74728] disabled:opacity-60">
              {envoi ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Envoyer l'invitation
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default InvitationPrivee;
